import { defineComponent as _defineComponent } from 'vue'
import {
  ref, computed, onBeforeUnmount, getCurrentInstance, onMounted,
} from 'vue';
import { DateRange } from '@/services/date-range';

import i18n from '@/plugins/i18n';
import store from '@/store/store';
import localized from '@/plugins/vue-localized-formatter/src/localized';

import ContactMessageDialog from '@/components/views/reservation/ContactMessageDialog.vue';
import MainToolbar from '@/components/views/main/MainToolbar.vue';
import DateRangePickerField from '@/components/pickers/DateRangePickerField.vue';
import FeedbackRatingChip from '@/components/views/feedback/FeedbackRatingChip.vue';
import FeedbackRatingSelect from '@/components/views/feedback/FeedbackRatingSelect.vue';
import FeedbackDetailsDialog from '@/components/views/feedback/FeedbackDetailsDialog.vue';
import { loadStoreModule, unloadStoreModule } from '@/services/module-loader';
import FeedbackStore from '@/store/feedback-store';
import { performDispatchAction } from '@/services/vue-utils';
import Feedback, { FeedbackFilter } from '@/grpc-api/model/feedback';
import debounce from 'lodash.debounce';
import RepeatableTimer from '@/services/repeatable-timer';
import PaywallUpgradeDialog from '@/components/dialogs/PaywallUpgradeDialog.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'Feedback',
  setup(__props) {


const isFeedbackFeatureAllowed = computed<boolean>(() => store.getters.isFeedbackFeatureAllowed);

const upgradeDialog = ref(false);

const refreshTimeout = 1 * 60 * 1000; // 1 min refresh timeout

const filter = computed(() => store.getters.feedbackFilter as FeedbackFilter);

const applyFilter = (value: FeedbackFilter) => {
  store.dispatch('feedbackApplyFilter', value);
};

const filterInactive = computed(() => filter.value.overallRating.length === 5
  && filter.value.ambienceRating.length === 5
  && filter.value.serviceRating.length === 5
  && filter.value.foodRating.length === 5
  && filter.value.name.length === 0
  && filter.value.comments.length === 0);

const detailsDialog = ref(false);
const messageDialog = ref(false);

const selectedFeedback = computed(() => store.getters.selectedFeedback as Feedback|undefined);

const showFeedbackDetail = async (f: Feedback) => {
  const feedback = await performDispatchAction('getFeedback', { id: f.id }) as Feedback | undefined;
  if (!feedback) return;
  store.dispatch('setSelectedFeedback', { feedback });
  detailsDialog.value = true;
};

const replyClicked = async (f: Feedback) => {
  const feedback = await performDispatchAction('getFeedback', { id: f.id }) as Feedback | undefined;
  if (!feedback) return;
  store.dispatch('setSelectedFeedback', { feedback });
  messageDialog.value = true;
};

const closeMessageDialog = async (res: boolean | undefined) => {
  if (res && selectedFeedback.value) {
    await performDispatchAction('getFeedback', { id: selectedFeedback.value.id }) as Feedback | undefined;
  }
  messageDialog.value = false;
  store.dispatch('setSelectedFeedback', { feedback: null });
};

const headers = [
  {
    text: i18n.t('label.date_of_feedback'), align: 'start', width: '200px', value: 'created',
  },
  {
    text: i18n.t('label.person_name'), align: 'start', width: '200px', value: 'name',
  },
  {
    text: i18n.t('label.comments'), value: 'comments', class: 'comments-col', cellClass: 'comments-col',
  },
  {
    text: i18n.t('label.rating_food'), value: 'foodRating', width: '140px', align: 'center',
  },
  {
    text: i18n.t('label.rating_service'), value: 'serviceRating', width: '140px', align: 'center',
  },
  {
    text: i18n.t('label.rating_ambience'), value: 'ambienceRating', width: '140px', align: 'center',
  },
  {
    text: i18n.t('label.rating_general'), value: 'overallRating', width: '140px', align: 'center',
  },
  {
    text: i18n.t('label.responded'), value: 'replied', width: '120px', align: 'center',
  },
];

const settings = computed(() => store.getters.feedbackSettings);

const feedbacks = computed(() => store.getters.feedbackVisibleFeedbacks as Feedback[]);

const feedbacksLength = computed(() => store.getters.feedbackVisibleCount as number);

const formatRepliedOnDate = (d: Date) => i18n.t('label.reply_sent_on_x', [localized.dateText(d)!]);

// filter date range
const filterDateRange = computed(() => store.getters.feedbackDateRange as DateRange);

const filterDateRangeSelected = async (dateRange: DateRange) => {
  performDispatchAction('loadFeedback', { dateRange }, { streamLoad: true });
};

const filterDateRangeDisabled = computed(() => store.getters.isFeedbackLoading as boolean);

const load = async () => {
  if (store.getters.isFeedbackLoaded) return;
  await performDispatchAction('loadFeedback', {}, { streamLoad: true });
  upgradeDialog.value = !isFeedbackFeatureAllowed.value;
};

// debounced refresh
const debouncedRefresh = debounce(async () => {
  try {
    await store.dispatch('refreshFeedback');
  } catch (e) {
    console.log('refresh feeedback error: ', e);
  }
}, 500, { leading: true, trailing: true, maxWait: 5000 });
const refresh = async () => debouncedRefresh();

const reset = async () => {
  store.dispatch('resetFeedback');
};

let timer = undefined as RepeatableTimer | undefined;

onMounted(() => {
  loadStoreModule(getCurrentInstance()?.proxy!, 'feedback', FeedbackStore, async () => { load(); });
  timer = new RepeatableTimer(async () => { await debouncedRefresh(); });
  timer.start(refreshTimeout);
});

onBeforeUnmount(() => {
  timer?.stop();
  unloadStoreModule(getCurrentInstance()?.proxy!, 'feedback', async () => { reset(); });
  // tryCloseEditReservation();
});

const pagination = (p :{
  itemsLength: number,
  itemsPerPage: number
  page: number,
  pageCount: number,
  pageStart: number,
  pageStop: number,
}) => {
  console.log('feedback pagination: ', p);
  store.dispatch('updateFeedbackPage', { pageSize: p.itemsPerPage, pageStart: p.pageStart });
};
const feedbackSortBy = computed(() => store.getters.feedbackSort.sortType as string);
const feedbackSortDesc = computed(() => store.getters.feedbackSort.sortDesc as boolean);
const updateSortBy = (field: string) => store.dispatch('updateFeedbackSort', { sortType: field });
const updateSortDesc = (desc: boolean) => store.dispatch('updateFeedbackSort', { sortDesc: desc });
const noSort = (items: Feedback[]) => items;


return { __sfc: true,isFeedbackFeatureAllowed, upgradeDialog, refreshTimeout, filter, applyFilter, filterInactive, detailsDialog, messageDialog, selectedFeedback, showFeedbackDetail, replyClicked, closeMessageDialog, headers, settings, feedbacks, feedbacksLength, formatRepliedOnDate, filterDateRange, filterDateRangeSelected, filterDateRangeDisabled, load, debouncedRefresh, refresh, reset, timer, pagination, feedbackSortBy, feedbackSortDesc, updateSortBy, updateSortDesc, noSort, localized, ContactMessageDialog, MainToolbar, DateRangePickerField, FeedbackRatingChip, FeedbackRatingSelect, FeedbackDetailsDialog, PaywallUpgradeDialog }
}

})